// @ts-nocheck
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { isPlano2026, taxaConservatoria } from "../../../helpers/process.helper";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import { addDays, differenceInDays, format } from "date-fns";
import { useCallback, useRef, useState } from "react";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import {
  CardPercent,
  CardStats,
  Comprovante,
  Delay,
  LinePercent,
  Prediction,
  StatsDispatch,
  Taxa,
  TaxaAcoes,
  TaxaButton,
  TaxaButtonSecundario,
  TaxaDestaque,
  TaxaNota,
} from "./styles";

const ACEITA_COMPROVANTE = ".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*";
const TAMANHO_MAXIMO_MB = 10;

export default function CardDispatch({ data }) {
  const { addToast } = useToast();

  // Plano 2026: a taxa da conservatória é do cliente. Ele paga pelo link
  // (PayPal, por tipo de processo) e anexa o comprovante aqui. Não há
  // validação sistêmica — a equipe confere no momento do envio; até lá o
  // comprovante pode ser trocado. Menor de idade não paga taxa; o que não
  // tem link (homologações) continua acertando com a equipe pelo WhatsApp.
  const plano2026 = isPlano2026(data?.sale?.family_plan);
  const taxa = plano2026 ? taxaConservatoria(data?.type) : { modo: "nenhum" };
  const [comprovante, setComprovante] = useState(data?.postal_fee_receipt || null);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);

  // O cliente conhece o PT do pedido (service_code) ou o da venda (client_id).
  const numeroPedido = data?.service_code || data?.sale?.client_id || "";
  const requerente = data?.applicant?.name || "";
  const mensagemTaxa = encodeURIComponent(
    `Olá! Meu processo ${numeroPedido}${requerente ? ` (${requerente})` : ""} está na etapa de despacho e quero pagar a taxa da conservatória. Podem me orientar sobre o valor e a forma de pagamento?`
  );
  const linkEquipe = `https://api.whatsapp.com/send?phone=5521990257203&text=${mensagemTaxa}`;

  const enviarComprovante = async (event) => {
    const arquivo = event.target.files && event.target.files[0];
    if (!arquivo) return;

    if (arquivo.size > TAMANHO_MAXIMO_MB * 1024 * 1024) {
      addToast({
        type: "error",
        title: "Arquivo muito grande",
        description: `Envie um comprovante de até ${TAMANHO_MAXIMO_MB} MB.`,
      });
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", arquivo);
    setEnviando(true);

    try {
      const response = await api.post(
        `central/processes/${data?.id}/postal-fee-receipt`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const processo = response?.data?.data || response?.data || {};
      setComprovante(processo?.postal_fee_receipt || null);
      addToast({
        type: "success",
        title: "Comprovante recebido",
        description: "Vamos conferir o pagamento no momento do envio do seu processo.",
      });
    } catch (err) {
      addToast({
        type: "error",
        title: "Não foi possível anexar",
        description:
          err?.response?.data?.message ||
          "Tente de novo em instantes. Se continuar, fale com o nosso suporte.",
      });
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const countDaysDispatch = differenceInDays(
    new Date(data?.post_prediction_date),
    new Date(data?.ready_to_post_date)
  );

  const totalPerProcess = parseInt(countDaysDispatch / 2);

  let startSendDay = new Date(data?.ready_to_post_date);
  let endSendDay = addDays(new Date(data?.ready_to_post_date), totalPerProcess);

  let startDispatchDay = endSendDay;
  let endDispatchDay = new Date(data?.post_prediction_date);

  let sendPercent = 0;
  let dispatchPercent = 0;

  if (new Date() - startSendDay > 0 && data?.step?.name !== "blocked_post") {
    sendPercent = parseInt(
      (Math.abs(new Date() - startSendDay) / (endSendDay - startSendDay)) * 100
    );

    if (sendPercent > 100 || !sendPercent) {
      sendPercent = 100;
    }

    if (sendPercent >= 100) {
      dispatchPercent = parseInt(
        (Math.abs(new Date() - startDispatchDay) /
          (endDispatchDay - startDispatchDay)) *
          100
      );
    }
  }

  const handleColor = useCallback((info) => {
    if (info > 100) {
      return "var(--atencao-50)";
    } else if (info > 0 && info <= 100) {
      return "var(--principal-50)";
    } else {
      return "var(--cinza-50)";
    }
  }, []);

  const textoPreparacao = !plano2026
    ? "Juntar todos os documentos; preenchimento e anexação do requerimento; pagamento de taxa da conservatória."
    : taxa.modo === "isento"
    ? "Juntar todos os documentos; preenchimento e anexação do requerimento."
    : "Juntar todos os documentos; preenchimento e anexação do requerimento; pagamento da taxa da conservatória (feito por você).";

  const inputComprovante = (
    <TaxaButtonSecundario aria-disabled={enviando}>
      <AttachFileIcon />
      {enviando
        ? "Enviando..."
        : comprovante
        ? "Trocar comprovante"
        : "Anexar comprovante"}
      <input
        ref={inputRef}
        type="file"
        accept={ACEITA_COMPROVANTE}
        disabled={enviando}
        onChange={enviarComprovante}
        data-testid="input-comprovante-vale"
      />
    </TaxaButtonSecundario>
  );

  return (
    <>
      <CardStats style={{ marginBottom: 0 }}>
        <p>despacho</p>
        <Prediction>
          <CalendarTodayOutlinedIcon
            style={
              data?.step?.name === "blocked_post"
                ? { color: "var(--erro-50)" }
                : { color: "var(--principal-50)" }
            }
          />
          <p>
            Previsão de envio para portugal:
            <strong>
              {data?.step?.name === "blocked_post"
                ? " Indefinido"
                : format(new Date(data?.post_prediction_date), " dd/MM/yyyy")}
            </strong>
          </p>
        </Prediction>
      </CardStats>
      <StatsDispatch>
        <p>Preparação para envio</p>
        <p className="analysisText">{textoPreparacao}</p>
        <CardPercent color={handleColor(sendPercent)}>
          <span className="bar">
            <LinePercent
              size={sendPercent > 100 ? 100 : sendPercent}
              color={handleColor(sendPercent)}
            />
          </span>
          <span className="percent">
            {sendPercent > 100 ? 80 : sendPercent}%
          </span>
        </CardPercent>

        {taxa.modo === "link" && comprovante && (
          <Comprovante>
            <div>
              <CheckCircleIcon />
              <p>Comprovante recebido — validando envio</p>
            </div>
            <p>
              Recebemos o seu comprovante
              {comprovante?.uploaded_at
                ? ` em ${format(new Date(comprovante.uploaded_at), "dd/MM/yyyy 'às' HH:mm")}`
                : ""}
              . A equipe confere o pagamento no momento do envio do processo para
              Portugal. Se precisar, você pode trocar o arquivo até o envio.
            </p>
            {comprovante?.url && (
              <a
                className="arquivo"
                href={comprovante.url}
                target="_blank"
                rel="noreferrer"
              >
                <InsertDriveFileOutlinedIcon fontSize="small" />
                {comprovante?.name || "Ver comprovante"}
              </a>
            )}
            <TaxaAcoes>{inputComprovante}</TaxaAcoes>
          </Comprovante>
        )}

        {taxa.modo === "link" && !comprovante && (
          <Taxa>
            <div>
              <AccessTimeIcon />
              <p>Aguardando o pagamento da taxa da conservatória</p>
            </div>
            <p>
              No seu plano, a taxa da conservatória portuguesa é paga por você.
              Pague pelo botão abaixo e, na descrição do pagamento, informe o
              nome do requerente e o número do pedido:
            </p>
            <TaxaDestaque>
              {requerente}
              {requerente && numeroPedido ? " — " : ""}
              {numeroPedido}
            </TaxaDestaque>
            <p>
              Depois, anexe aqui o comprovante. O pagamento é conferido no
              momento do envio do seu processo para Portugal.
            </p>
            <TaxaAcoes>
              <TaxaButton href={taxa.link} target="_blank" rel="noreferrer">
                <OpenInNewIcon />
                Pagar a taxa da conservatória
              </TaxaButton>
              {inputComprovante}
            </TaxaAcoes>
            <TaxaNota>
              Aceitamos PDF ou imagem (JPG, PNG) de até {TAMANHO_MAXIMO_MB} MB.
            </TaxaNota>
          </Taxa>
        )}

        {taxa.modo === "equipe" && (
          <Taxa>
            <div>
              <AccessTimeIcon />
              <p>Aguardando o pagamento da taxa da conservatória</p>
            </div>
            <p>
              No seu plano, a taxa da conservatória portuguesa é paga por você.
              Enquanto preparamos o malote, fale com a nossa equipe para acertar
              o pagamento: a gente informa o valor e a forma de pagar e, assim que
              confirmado, o seu processo segue para Portugal.
            </p>
            <TaxaButton href={linkEquipe} target="_blank" rel="noreferrer">
              <WhatsAppIcon />
              Falar com a equipe para pagar a taxa
            </TaxaButton>
          </Taxa>
        )}
      </StatsDispatch>
      <StatsDispatch>
        <p>Despacho dos documentos</p>
        <p className="analysisText">
          Preparação do malote; transferência para operador logístico; despacho
          do malote.
        </p>
        <CardPercent color={handleColor(dispatchPercent)}>
          <span className="bar">
            <LinePercent
              size={dispatchPercent > 100 ? 80 : dispatchPercent}
              color={handleColor(dispatchPercent)}
            />
          </span>
          <span className="percent">
            {dispatchPercent > 100 ? 80 : dispatchPercent}%
          </span>
        </CardPercent>
        {data &&
          !!data?.reanalysis_date &&
          new Date(endDispatchDay) - new Date() < 0 && (
            <Delay color="var(--atencao-50)">
              <div>
                <AccessTimeIcon />
                <p>O despacho atrasou um pouco</p>
              </div>
              <p>
                O operador logístico apresentou um novo prazo para coleta do seu
                malote.
              </p>
            </Delay>
          )}
      </StatsDispatch>
    </>
  );
}
